export type RequestStatus = 'recue' | 'en-traitement' | 'resolue'

export interface TimelineStep {
  date: string
  label: string
}

export interface RequestAttachment {
  name: string
  /** Drives the icon shown next to the file. */
  type: 'image' | 'pdf' | 'form'
  size: string
  /** Aperçu affiché pour les photos. */
  url?: string
}

export type MessageAuthor = 'citoyen' | 'ville'

export interface RequestMessage {
  id: string
  author: MessageAuthor
  authorName: string
  sentAt: string
  body: string
  attachment?: RequestAttachment
  /** Set when the Ville expects something back from the citizen. */
  awaiting?: 'document' | 'info'
  /** Accusé de lecture d'un message du citoyen par la Ville. */
  readAt?: string
  /** Message de clôture : la Ville invite à évaluer la qualité du service. */
  satisfaction?: boolean
}

export interface CitizenRequest {
  id: string
  reference: string
  title: string
  /** Service municipal responsable (ex. Travaux publics). */
  service: string
  /** Sous-catégorie de la requête (ex. Collecte des déchets). */
  category: string
  address: string
  fullAddress: string
  submittedOn: string
  createdOn: string
  resolvedOn: string | null
  createdBy: string
  status: RequestStatus
  description: string
  attachments: RequestAttachment[]
  timeline: TimelineStep[]
  messages: RequestMessage[]
  response?: string
}

export const statusLabels: Record<RequestStatus, string> = {
  recue: 'Reçue',
  'en-traitement': 'En traitement',
  resolue: 'Résolue',
}

export const statusOrder: RequestStatus[] = ['recue', 'en-traitement', 'resolue']

export const quickReports = ['Nid-de-poule', 'Éclairage', 'Arbre', 'Eau', 'Collectes']

export const profileAddress = '366, chemin Lakeshore'

/** Photo simulée jointe à un signalement de nid-de-poule (aucun fichier n'est lu). */
export const potholePhotoUrl =
  'https://cdn.magicpatterns.com/patterns/generated-images/c585eb28-7d3b-4836-bd45-054c1de544e0.jpg'

/** Photo simulée jointe à un signalement de fontaine qui fuit (démo depuis la carte). */
export const fountainPhotoUrl =
  'https://cdn.magicpatterns.com/patterns/generated-images/6a7ae30c-b278-4416-822e-d19dba0c008a.jpg'

/** Photos simulées par type de problème : le « téléversement » affiche l'image au lieu d'un texte. */
const cdn = (id: string) => `https://cdn.magicpatterns.com/patterns/generated-images/${id}.jpg`

export const intentPhotos: Record<string, string> = {
  // Voirie et chaussée
  'Nid-de-poule': potholePhotoUrl,
  'Affaissement de chaussée': cdn('3f30051a-880a-4d06-8173-3c3e50992bf4'),
  'Trottoir endommagé': cdn('f697ed45-94c9-4062-a5d9-03ecca4b8243'),
  // Passage pour piétons effacé devant une école (démo).
  'Marquage de rue effacé': cdn('e6856f99-0e89-4e04-b185-7e9024aaf89a'),
  // Éclairage public
  'Lampadaire éteint': cdn('b722ae50-e0b5-4d14-b57f-1072fe5feb74'),
  'Lampadaire qui clignote': cdn('64245f93-c8b1-4ac7-958e-34b8f9451221'),
  'Lampadaire allumé le jour': cdn('b38fc553-0fb2-468a-b80c-b9c3c4912498'),
  'Lampadaire défectueux': cdn('b722ae50-e0b5-4d14-b57f-1072fe5feb74'),
  // Arbres et parcs
  'Branche tombée': cdn('b85e4b86-bc12-400d-b0ee-e98f32b2013f'),
  'Arbre à élaguer': cdn('b8a476e9-fdd9-40a8-a4bf-f73cab3eb726'),
  'Arbre malade': cdn('5ee49142-2117-41e6-bdc2-9a639383e571'),
  'Équipement de parc brisé': cdn('4fae4d87-f2e2-4ecd-a015-6ed76a2e837e'),
  'Arbre ou branche à dégager': cdn('b85e4b86-bc12-400d-b0ee-e98f32b2013f'),
  // Eau et égouts
  'Fontaine d’eau qui fuit': fountainPhotoUrl,
  'Fuite d’eau': cdn('ffa8f81c-428e-49c0-a05d-ac11aa401df5'),
  'Borne-fontaine endommagée': cdn('8c95bba0-e830-449f-90b2-622c1a525209'),
  'Égout refoulé': cdn('79d5d54c-c45f-4e53-9c08-5358bbac9c7d'),
  'Faible pression d’eau': cdn('194fcc04-7eb9-41f7-bf94-22d966662ba2'),
  // Collectes
  'Bac non ramassé': cdn('8693855a-989f-4a93-9420-ae4fe84fa033'),
  'Bac brisé': cdn('9170e658-800e-46fa-bdea-86415ce432f5'),
  'Demande de nouveau bac': cdn('10022e5b-88a8-4844-9f43-2253a3bfdaa2'),
  // Animaux
  'Animal perdu': cdn('0bcdf528-76c2-4d27-a4b7-077cc32ee59b'),
  'Animal trouvé': cdn('35af41b3-f935-4d41-9d28-6ff3e84e87cb'),
  'Animal errant': cdn('453ce5b1-fa46-4c62-bfd9-9dbf4769aeed'),
  'Nuisance animale': cdn('a9b90248-151c-4ed9-946f-781933a0a11b'),
  // Déneigement
  'Rue non déneigée': cdn('5f80f0b1-df46-4a27-a6f8-293ea8be3d30'),
  'Trottoir glacé': cdn('e55412d3-4f14-46ed-b67d-b730e2e6a1fa'),
  'Neige sur le terrain': cdn('1f9a6dff-918e-404d-91a6-f22995f10a62'),
  // Autres
  'Nuisance ou bruit': cdn('0ff8cf78-cee0-449e-a577-1de258e73c7f'),
  Graffiti: cdn('6b50db12-41f4-4860-8c69-30766e04f5da'),
  'Autre problème': cdn('b220ec8b-d9e0-42b5-8b3c-b8130ed04eff'),
}

/** Catégories du formulaire de demande manuelle. */
export const manualCategories = [
  {
    id: 'voirie',
    label: 'Voirie et chaussée',
    service: 'Travaux publics',
    types: ['Nid-de-poule', 'Affaissement de chaussée', 'Trottoir endommagé', 'Marquage de rue effacé'],
  },
  {
    id: 'eclairage',
    label: 'Éclairage public',
    service: 'Travaux publics',
    types: ['Lampadaire éteint', 'Lampadaire qui clignote', 'Lampadaire allumé le jour'],
  },
  {
    id: 'arbres',
    label: 'Arbres et parcs',
    service: 'Parcs et espaces verts',
    types: ['Branche tombée', 'Arbre à élaguer', 'Arbre malade', 'Équipement de parc brisé'],
  },
  {
    id: 'eau',
    label: 'Eau et égouts',
    service: 'Aqueduc',
    types: ['Fuite d’eau', 'Borne-fontaine endommagée', 'Égout refoulé', 'Faible pression d’eau'],
  },
  {
    id: 'collectes',
    label: 'Collectes',
    service: 'Travaux publics',
    types: ['Bac non ramassé', 'Bac brisé', 'Demande de nouveau bac'],
  },
  {
    id: 'animaux',
    label: 'Animaux',
    service: 'Service animalier',
    types: ['Animal perdu', 'Animal trouvé', 'Animal errant', 'Nuisance animale'],
  },
  {
    id: 'deneigement',
    label: 'Déneigement',
    service: 'Travaux publics',
    types: ['Rue non déneigée', 'Trottoir glacé', 'Neige sur le terrain'],
  },
  {
    id: 'autre',
    label: 'Autre',
    service: 'Service aux citoyens',
    types: ['Nuisance ou bruit', 'Graffiti', 'Autre problème'],
  },
] as const

/** Adresses du profil proposées en un clic. */
export const manualAddresses = [
  { id: 'residence', label: 'Résidence', address: '366, chemin Lakeshore', postal: 'H9W 4S4' },
] as const

/** Position « détectée » par la géolocalisation simulée. */
export const detectedPosition = 'Avenue Woodland'

/** Adresses et intersections de Beaconsfield proposées en autocomplétion. */
export const addressSuggestions = [
  '100, Harwood Gate',
  '130, Taywood Drive',
  '15, City Lane',
  '225, avenue Evergreen',
  '288, boulevard Beaconsfield',
  '303, rue Sherbrooke',
  '340, Preston Drive',
  '366, chemin Lakeshore',
  '422, boulevard Beaconsfield',
  '450, chemin Lakeshore',
  '50, avenue Willowbrook',
  '538, Beaurepaire Drive',
  '82, Devon Road',
  'Boulevard Beaconsfield et City Lane',
  'Chemin Lakeshore et avenue Woodland',
  'Beaurepaire Drive et Devon Road',
]

/** Very small keyword classifier used to simulate ATNA's understanding. */
export const intents = [
  {
    // Avant « Nid-de-poule » : « marquage de rue » contient « rue » mais n'est pas un trou.
    keywords: ['marquage', 'passage piéton', 'passage pour piétons', 'passage pieton', 'traverse', 'ligne effacée', 'lignes effacées'],
    label: 'Marquage de rue effacé',
    service: 'Voirie',
    question: 'Où se trouve le marquage effacé ?',
    answers: ['Passage pour piétons près d’une école', 'Ligne de circulation', 'Ligne d’arrêt'],
    photoHint: true,
  },
  {
    keywords: ['nid-de-poule', 'nid de poule', 'trou', 'asphalte', 'route', 'chaussée'],
    label: 'Nid-de-poule',
    service: 'Voirie',
    question: 'Quelle est la taille approximative du nid-de-poule ?',
    answers: ['Petit (moins de 30 cm)', 'Moyen (30 à 60 cm)', 'Grand (plus de 60 cm)'],
    photoHint: true,
  },
  {
    keywords: ['lampadaire', 'lumière', 'éclairage', 'ampoule'],
    label: 'Lampadaire défectueux',
    service: 'Éclairage public',
    question: 'Le lampadaire est-il complètement éteint ou clignote-t-il ?',
    answers: ['Complètement éteint', 'Il clignote', 'Il reste allumé le jour'],
    photoHint: false,
  },
  {
    keywords: ['arbre', 'branche', 'tombé'],
    label: 'Arbre ou branche à dégager',
    service: 'Parcs et espaces verts',
    question: 'L’arbre bloque-t-il la circulation ?',
    answers: ['Oui, la rue est bloquée', 'Non, sur le trottoir', 'Non, sur un terrain'],
    photoHint: true,
  },
  {
    // Avant « Fuite d'eau » : « fontaine à eau qui fuit » doit être reconnue comme fontaine.
    keywords: ['fontaine', 'abreuvoir'],
    label: 'Fontaine d’eau qui fuit',
    service: 'Parcs et espaces verts',
    question: 'L’eau coule-t-elle en continu ?',
    answers: ['Oui, en continu', 'Goutte à goutte', 'Seulement quand on l’utilise'],
    photoHint: true,
  },
  {
    keywords: ['eau', 'fuite', 'coule', 'aqueduc', 'trottoir mouillé'],
    label: 'Fuite d’eau',
    service: 'Aqueduc',
    question: 'L’écoulement est-il continu ?',
    answers: ['Oui, en continu', 'Par intermittence', 'Je ne sais pas'],
    photoHint: true,
  },
  {
    keywords: ['bac', 'collecte', 'ordures', 'recyclage', 'ramassé'],
    label: 'Bac non ramassé',
    service: 'Collectes',
    question: 'Quel bac n’a pas été ramassé ?',
    answers: ['Recyclage', 'Ordures', 'Matières organiques'],
    photoHint: false,
  },
]

export const emergencyKeywords = [
  'fil électrique',
  'fil electrique',
  'électrocut',
  'feu',
  'incendie',
  'gaz',
  'blessé',
  'accident',
  'fumée',
]

export const requests: CitizenRequest[] = [
  {
    id: 'req-2903',
    reference: '#REQ-2903',
    title: 'Animal trouvé',
    service: 'Service animalier',
    category: 'Animal errant ou trouvé',
    address: 'Parc Centennial, Beaconsfield',
    fullAddress: '288, boulevard Beaconsfield, Beaconsfield, Québec, H9W 4A7',
    submittedOn: 'Envoyée aujourd’hui',
    createdOn: '06/10/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'recue',
    description:
      'Chien trouvé sans médaille lisible près de l’aire de jeux du parc. L’animal est calme et gardé en laisse en attendant une prise en charge.',
    attachments: [{ name: 'chien-trouve-parc.jpg', type: 'image', size: '2,1 Mo' }],
    timeline: [{ date: 'Aujourd’hui', label: 'Signalement reçu' }],
    messages: [
      {
        id: 'msg-2903-1',
        author: 'ville',
        authorName: 'Service animalier',
        sentAt: 'Aujourd’hui, 9 h 42',
        body: 'Merci pour votre signalement. Un patrouilleur animalier est assigné et vous contactera si l’animal est toujours sur place.',
      },
    ],
  },
  {
    id: 'req-2790',
    reference: '#REQ-2790',
    title: 'Lampadaire défectueux',
    service: 'Travaux publics',
    category: 'Éclairage public',
    address: '412, chemin Lakeshore',
    fullAddress: '412, chemin Lakeshore, Beaconsfield, Québec, H9W 4S4',
    submittedOn: 'Envoyée le 23 septembre',
    createdOn: '23/09/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'en-traitement',
    description:
      'Le lampadaire devant le 412 restait éteint la nuit, laissant le trottoir dans le noir entre les deux intersections.',
    attachments: [{ name: 'lampadaire-412-lakeshore.jpg', type: 'image', size: '1,6 Mo' }],
    timeline: [
      { date: '23 septembre', label: 'Demande reçue' },
      { date: '24 septembre', label: 'Transmise au service de l’éclairage' },
      { date: '25 septembre', label: 'En traitement' },
      { date: '28 septembre', label: 'Intervention planifiée' },
    ],
    messages: [
      {
        id: 'msg-2790-1',
        author: 'ville',
        authorName: 'Ville de Beaconsfield',
        sentAt: '24 septembre, 10 h 15',
        body: 'Bonjour, votre signalement a été transmis à l’équipe d’éclairage public. Une vérification est prévue cette semaine.',
      },
      {
        id: 'msg-2790-2',
        author: 'citoyen',
        authorName: 'Vous',
        sentAt: '25 septembre, 8 h 03',
        readAt: '25 septembre, 9 h 17',
        body: 'Merci. Le lampadaire voisin, devant le 420, commence aussi à clignoter depuis hier soir.',
      },
      {
        id: 'msg-2790-3',
        author: 'ville',
        authorName: 'Ville de Beaconsfield',
        sentAt: '26 septembre, 14 h 30',
        body: 'Merci pour la précision. Pouvez-vous nous indiquer le numéro inscrit sur la plaque du poteau ? Il nous aide à identifier le bon circuit électrique.',
        awaiting: 'info',
      },
    ],
  },
  {
    id: 'req-2715',
    reference: '#REQ-2715',
    title: 'Panneau d’arrêt endommagé',
    service: 'Travaux publics',
    category: 'Signalisation routière',
    address: 'Beaurepaire Drive, angle Devon Road',
    fullAddress: 'Beaurepaire Drive, angle Devon Road, Beaconsfield, Québec, H9W 3B2',
    submittedOn: 'Envoyée le 14 septembre',
    createdOn: '14/09/26',
    resolvedOn: '22/09/26',
    createdBy: 'ATNA',
    status: 'resolue',
    description:
      'Le panneau d’arrêt au coin de Devon Road est plié vers le sol et n’est plus visible pour les automobilistes qui arrivent de l’ouest.',
    attachments: [{ name: 'panneau-arret-devon.jpg', type: 'image', size: '1,4 Mo' }],
    timeline: [
      { date: '14 septembre', label: 'Demande reçue' },
      { date: '15 septembre', label: 'Transmise à l’équipe de signalisation' },
      { date: '18 septembre', label: 'En traitement' },
      { date: '22 septembre', label: 'Panneau remplacé · Requête résolue' },
    ],
    messages: [
      {
        id: 'msg-2715-1',
        author: 'ville',
        authorName: 'Ville de Beaconsfield',
        sentAt: '15 septembre, 9 h 05',
        body: 'Bonjour, votre signalement a été transmis à l’équipe de signalisation. Une intervention est prévue dans les prochains jours.',
      },
      {
        id: 'msg-2715-2',
        author: 'ville',
        authorName: 'Ville de Beaconsfield',
        sentAt: '22 septembre, 13 h 40',
        body: 'Merci pour votre signalement ! Le panneau d’arrêt de Devon Road a été remplacé et est maintenant bien visible. N’hésitez pas à nous dire ce que vous pensez de la qualité du service.',
        satisfaction: true,
      },
    ],
    response: 'Le panneau d’arrêt a été remplacé le 22 septembre.',
  },
]

/**
 * Requête créée par ATNA à la fin d'un signalement : fraîchement reçue, sans message.
 * Elle n'apparaît dans « Mes demandes » qu'une fois le signalement envoyé.
 */
export function buildReportedRequest(details: {
  title: string
  service: string
  address: string
  description: string
  hasPhoto: boolean
}): CitizenRequest {
  return {
    id: 'req-2842',
    reference: '#REQ-2842',
    title: details.title,
    service: details.service,
    category: details.title === 'Nid-de-poule' ? 'Voirie et chaussée' : details.service,
    address: details.address,
    fullAddress: `${details.address}, Beaconsfield, Québec`,
    submittedOn: 'Envoyée aujourd’hui',
    createdOn: '06/10/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'recue',
    description: details.description,
    attachments: details.hasPhoto
      ? [
          {
            name: 'photo-signalement.jpg',
            type: 'image',
            size: '1,9 Mo',
            url: intentPhotos[details.title],
          },
        ]
      : [],
    timeline: [{ date: 'Aujourd’hui', label: 'Demande reçue' }],
    messages: [],
  }
}

export type PublicReportCategory = 'danger' | 'voirie' | 'eclairage' | 'arbre' | 'eau'

/** Catégorie de carte d'un problème reconnu par ATNA. */
export function reportCategoryFor(intentLabel: string): PublicReportCategory {
  if (intentLabel.startsWith('Lampadaire')) return 'eclairage'
  if (intentLabel.startsWith('Arbre')) return 'arbre'
  if (intentLabel.includes('eau') || intentLabel.includes('Fontaine')) return 'eau'
  return 'voirie'
}

export type PublicReportStatus = 'nouveau' | 'en-traitement' | 'resolue'

export interface PublicReport {
  id: string
  category: PublicReportCategory
  title: string
  address: string
  distance: string
  reportedAgo: string
  status: PublicReportStatus
  confirmations: number
  lat: number
  lng: number
}

export const reportCategories: Record<PublicReportCategory, { label: string; color: string }> = {
  danger: { label: 'Danger', color: '#d93b30' },
  voirie: { label: 'Voirie', color: '#ec9522' },
  eclairage: { label: 'Éclairage', color: '#b45309' },
  arbre: { label: 'Arbres', color: '#5e9c2d' },
  eau: { label: 'Eau', color: '#2f5bc9' },
}

export const publicReportStatusLabels: Record<PublicReportStatus, string> = {
  nouveau: 'Nouveau',
  'en-traitement': 'Équipe en route',
  resolue: 'Réglé',
}

/** Position approximative du domicile au profil (sud de l'autoroute 20, Beaconsfield). */
export const profileCoordinates: [number, number] = [45.4252, -73.8672]

export const publicReports: PublicReport[] = [
  {
    id: 'pub-1',
    category: 'voirie',
    title: 'Nid-de-poule profond',
    address: 'Chemin Lakeshore, près de l’avenue Woodland',
    distance: '300 m',
    reportedAgo: 'il y a 25 min',
    status: 'nouveau',
    confirmations: 7,
    lat: 45.4258,
    lng: -73.87,
  },
  {
    id: 'pub-2',
    category: 'eclairage',
    title: 'Lampadaire éteint',
    address: '412, chemin Lakeshore',
    distance: '450 m',
    reportedAgo: 'il y a 2 h',
    status: 'en-traitement',
    confirmations: 12,
    lat: 45.4248,
    lng: -73.8655,
  },
  {
    id: 'pub-3',
    category: 'arbre',
    title: 'Branche tombée sur le trottoir',
    address: 'Parc Centennial',
    distance: '650 m',
    reportedAgo: 'il y a 40 min',
    status: 'nouveau',
    confirmations: 4,
    lat: 45.4298,
    lng: -73.8644,
  },
  {
    id: 'pub-4',
    category: 'voirie',
    title: 'Affaissement de chaussée',
    address: 'Beaurepaire Drive, angle Devon Road',
    distance: '1,1 km',
    reportedAgo: 'il y a 5 h',
    status: 'en-traitement',
    confirmations: 18,
    lat: 45.428,
    lng: -73.8715,
  },
  {
    id: 'pub-5',
    category: 'eclairage',
    title: 'Lampadaire qui clignote',
    address: 'Avenue Elm',
    distance: '800 m',
    reportedAgo: 'il y a 1 h',
    status: 'nouveau',
    confirmations: 9,
    lat: 45.4325,
    lng: -73.876,
  },
  {
    id: 'pub-6',
    category: 'danger',
    title: 'Feu de circulation hors service',
    address: 'Boulevard Beaconsfield, angle City Lane',
    distance: '1,4 km',
    reportedAgo: 'il y a 15 min',
    status: 'en-traitement',
    confirmations: 31,
    lat: 45.434,
    lng: -73.859,
  },
  {
    id: 'pub-7',
    category: 'voirie',
    title: 'Marquage de rue effacé',
    address: 'Rue Sherbrooke',
    distance: '1,6 km',
    reportedAgo: 'hier',
    status: 'resolue',
    confirmations: 3,
    lat: 45.4345,
    lng: -73.8605,
  },
  {
    id: 'pub-8',
    category: 'arbre',
    title: 'Arbre à élaguer près des fils',
    address: 'Preston Drive',
    distance: '2,0 km',
    reportedAgo: 'il y a 3 h',
    status: 'nouveau',
    confirmations: 6,
    lat: 45.4352,
    lng: -73.8688,
  },
]

export const detailQuestions = [
  'Où en est ma demande ?',
  'Quand sera-t-elle traitée ?',
  'Est-ce que je dois faire autre chose ?',
]
